import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { userStatusService } from '../index';
import { HttpPromiseState, isNullOrUndefined, watchHttpPromise } from '../../lib/helpers';
import axios from 'axios';
import { responseUtils } from 'domain/utils/responseUtils';
import { FailResponse } from 'domain/models/response-model';
import Swal from 'sweetalert2';
import q from 'q';
import to from 'await-to-js';

const myAxios = axios.create();
const unauthenticatedStatus = 401;

function hasResponse(error: AxiosError): error is AxiosError & { response: AxiosResponse } {
    return !isNullOrUndefined(error.response);
}

function isUnauthenticatedError(error: AxiosError): boolean {
    if (!hasResponse(error))
        return false;
    return error.response.status === unauthenticatedStatus;
}

async function logoutOnUnauthorized(error: AxiosError, delay: q.Promise<void> | null = null) {
    await delay;
    if (!error.response) {
        return;
    }
    if (error.response.status == 401) {
        userStatusService.setLoggedOut();
        return;
    }
    if (responseUtils.isFailResponse(error.response.data)) {
        const failResponse: FailResponse = error.response.data;
        var msg = failResponse.Errors[0].Message;
        Swal.fire({ icon: 'error', text: msg });
        return;
    }
}

const withCredentials: AxiosRequestConfig = { withCredentials: true };

function applyWithCredentials(config: AxiosRequestConfig | undefined): AxiosRequestConfig {
    if (config === undefined)
        return withCredentials;
    config.withCredentials = true;
    return config;
}

/******************************************************************************/

async function httpGetAxiosResponse<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    var delay = q.delay(400);
    config = applyWithCredentials(config);
    const promise: Promise<AxiosResponse<T>> = myAxios.get(url, config);
    promise.catch((error) => {
        logoutOnUnauthorized(error, delay);
    });
    await delay;
    return promise;
}

async function handleAxiosErrorWithSweetAlert(promise: Promise<AxiosResponse>) {

    const [err, resp]: [AxiosError<any> | null, undefined] | [null, AxiosResponse] = await to(promise);
    if (err) {
        let errMessage: string;
        if (err.response?.status == 400 && err.response.data.detail) {
            errMessage = err.response.data.detail;
        } else if (err.response?.status == 422 && err.response.data.detail) {
            errMessage = err.response.data.detail;
        } else {
            errMessage = err.message;
        }
        Swal.fire({ icon: 'error', text: errMessage });
        return Promise.reject();
    }
    return Promise.resolve(resp!);
}

async function httpGetWithSweetAlert<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    Swal.showLoading();
    var delay = q.delay(400);
    var promise = httpGetAxiosResponse<T>(url, config);
    await delay;
    return handleAxiosErrorWithSweetAlert(promise);
}

async function httpPostAxiosResponse<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    var delay = q.delay(400);
    config = applyWithCredentials(config);
    let promise: Promise<AxiosResponse<T>> = myAxios.post(url, data, config);
    promise.catch((error) => {
        logoutOnUnauthorized(error, delay);
    });
    await delay;
    return promise;
}

async function httpPostWithSweetAlert<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    Swal.showLoading();
    var delay = q.delay(400);
    var promise = httpPostAxiosResponse<T>(url, data, config);
    await delay;
    return handleAxiosErrorWithSweetAlert(promise);
}

function httpPutAxiosResponse<T>(url: string, data?: any, headers?: any): Promise<AxiosResponse<T>> {
    if(headers) withCredentials.headers = headers;
    let promise: Promise<AxiosResponse<T>> = myAxios.put(url, data, withCredentials);
    promise.catch(logoutOnUnauthorized);
    return promise;
}

function httpDeleteAxiosResponse<T>(url: string): Promise<AxiosResponse<T>> {
    let promise: Promise<AxiosResponse<T>> = myAxios.delete(url, withCredentials);
    promise.catch(logoutOnUnauthorized);
    return promise;
}

class WithAxiosResponse {
    readonly get = httpGetAxiosResponse;
    readonly post = httpPostAxiosResponse;
    readonly put = httpPutAxiosResponse;
    readonly delete = httpDeleteAxiosResponse;
    readonly getWithSweetAlert = httpGetWithSweetAlert;
    readonly postWithSweetAlert = httpPostWithSweetAlert;
}

/******************************************************************************/

function watchHttpGetAxios<T>(url: string,
    config?: AxiosRequestConfig): HttpPromiseState<T> {
    let promise: Promise<AxiosResponse<T>> = httpGetAxiosResponse<T>(url, config);
    return watchHttpPromise(promise);
}

function watchHttpPostAxios<T>(url: string,
    data?: any,
    config?: AxiosRequestConfig): HttpPromiseState<T> {
    let promise: Promise<AxiosResponse<T>> = httpPostAxiosResponse<T>(url, data, config);
    return watchHttpPromise(promise);
}

function watchHttpPutAxios<T>(url: string,
    data?: any, headers?: any): HttpPromiseState<T> {
    let promise: Promise<AxiosResponse<T>> = httpPutAxiosResponse<T>(url, data, headers);
    return watchHttpPromise(promise);
}

function watchHttpDeleteAxios<T>(url: string): HttpPromiseState<T> {
    let promise = httpDeleteAxiosResponse<T>(url);
    return watchHttpPromise(promise);
}

class WithHttpState {
    readonly get = watchHttpGetAxios;
    readonly post = watchHttpPostAxios;
    readonly put = watchHttpPutAxios;
    readonly delete = watchHttpDeleteAxios;
}

/******************************************************************************/

class HttpClient {
    readonly axios = new WithAxiosResponse();
    readonly state = new WithHttpState();
}

export const httpClient = new HttpClient();
